import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { get, post } from '@/shared/api/api-client';
import type { EducationWorkExperience } from '@/types/settings-api';

/**
 * Practitioner Educational Details — PROD-761 commit 4.
 *
 * GET  /api/EducationWorkExperience/{doctorSetupId}  — fetch
 * POST /api/EducationWorkExperience                  — save (full record)
 *
 * Body shape verified against working dev cURLs 2026-05-13. Save is
 * symmetric — POST sends the full nested record; backend replaces.
 *
 * Wire-shape notes (see settings-api.ts for full audit):
 * - TYPO'D field names verbatim: `orgnizationName` / `orgnizationId`.
 * - `duration` is a YEAR range (integers), not dates. Null end = ongoing.
 * - Qualification uses `issuer` (school GUID) + `degree` (degree GUID).
 * - Residency/Fellowship use `orgnizationId` (hospital GUID) +
 *   `specialityId` + `code` (SNOMED).
 */
/**
 * The backend sends `null` (not `[]`) for any section the practitioner
 * has no rows in. Every consumer reads `.length` / `.map` on these, so a
 * null crashes the whole tab (PROD-3203 F2). Arrays are made safe here,
 * once, at the data layer.
 */
type EducationArrayField =
  | 'qualifications'
  | 'workExperiences'
  | 'residencies'
  | 'fellowships'
  | 'achievements';

export type EducationWorkExperienceWire = Omit<
  EducationWorkExperience,
  EducationArrayField
> & {
  [K in EducationArrayField]: EducationWorkExperience[K] | null | undefined;
};

export function normaliseEducationWorkExperience(
  raw: EducationWorkExperienceWire,
): EducationWorkExperience {
  return {
    ...raw,
    qualifications: raw.qualifications ?? [],
    workExperiences: raw.workExperiences ?? [],
    residencies: raw.residencies ?? [],
    fellowships: raw.fellowships ?? [],
    achievements: raw.achievements ?? [],
  };
}

export function useEducationWorkExperience(
  doctorSetupId: string | null | undefined,
) {
  return useQuery({
    queryKey: ['settings', 'education-work-experience', doctorSetupId],
    queryFn: ({ signal }) => {
      if (!doctorSetupId) {
        throw new Error(
          'useEducationWorkExperience called without doctorSetupId',
        );
      }
      return get<EducationWorkExperienceWire>(
        `/api/EducationWorkExperience/${doctorSetupId}`,
        { signal },
      ).then(normaliseEducationWorkExperience);
    },
    enabled: !!doctorSetupId,
  });
}

export function useSaveEducationWorkExperience() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (record: EducationWorkExperience) =>
      post<EducationWorkExperience>('/api/EducationWorkExperience', record),
    onSuccess: (_data, vars) => {
      // Invalidating by practitionerId rather than doctorSetupId because
      // the GET is keyed on doctorSetupId — but after a refetch we need
      // to refresh the same key. Just invalidate the whole prefix.
      queryClient.invalidateQueries({
        queryKey: ['settings', 'education-work-experience'],
      });
      void vars;
    },
  });
}
